/**
 * NotificationBell — Sources Spine Phase 2 PR 22.
 *
 * The header bell + dropdown panel for in-app notifications. Reads
 * from `/api/notifications` (active alert-kind action_items with
 * per-user read state). The count badge is refreshed on a slow poll
 * (60s) so the bell stays current without being noisy; the list is
 * fetched fresh on open.
 *
 * Replaces the legacy bell that read from `useDataStore.alerts` —
 * the legacy data store predates the unified `action_items` table
 * and didn't carry per-user read state.
 *
 * Mark-read semantics:
 *   - Clicking a notification row marks it read for the current user
 *     and navigates to the row's `internalActionLink` (typically the
 *     source detail page).
 *   - "Mark all read" calls the bulk endpoint so heavy-volume
 *     accounts can clear the badge in one round-trip.
 *   - "Mark all read" affects only the current user; the team's
 *     view of the alert (snoozed / completed / dismissed) is
 *     unchanged and still managed from the Action Items page.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Loader2, Check } from "lucide-react";
import { api, type Notification } from "@/lib/api";

const COUNT_REFRESH_MS = 60_000;

interface NotificationBellProps {
  /** Optional: lets the AppShell coordinate "only one dropdown open" with
   *  other header dropdowns. When omitted the bell manages its own open
   *  state. */
  isOpen?: boolean;
  onToggle?: (next: boolean) => void;
}

export function NotificationBell({ isOpen, onToggle }: NotificationBellProps) {
  // The dropdown's open state can be controlled (AppShell-coordinated) or
  // self-managed. Internal state is the fallback when the parent doesn't
  // pass isOpen.
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    onToggle?.(next);
    if (isOpen === undefined) setInternalOpen(next);
  };

  const [unreadCount, setUnreadCount] = useState(0);
  const [capped, setCapped] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();

  // Refresh the count badge on mount + every 60s while mounted.
  // Polling is intentionally slow — the bell is a passive surface;
  // real-time push would be nice but isn't worth the websocket cost.
  useEffect(() => {
    let cancelled = false;
    async function refreshCount() {
      try {
        const r = await api.getNotificationCount();
        if (!cancelled) {
          setUnreadCount(r.unread);
          setCapped(r.capped);
        }
      } catch {
        // The bell is best-effort; swallow transient failures so the
        // header doesn't render an error chrome.
      }
    }
    refreshCount();
    const id = window.setInterval(refreshCount, COUNT_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Fetch the list on open. Re-fetched each time the dropdown opens so
  // a user who left it closed for hours sees fresh data on next click.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setListLoading(true);
    api
      .getNotifications({ status: "active", limit: 20 })
      .then((r) => {
        if (!cancelled) setNotifications(r.notifications);
      })
      .catch(() => {
        if (!cancelled) setNotifications([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleClickNotification(n: Notification) {
    setOpen(false);
    // Optimistic local update: mark read in the in-memory list so the
    // dropdown doesn't reopen showing the row still bolded.
    setNotifications((prev) =>
      prev.map((x) =>
        x.id === n.id
          ? { ...x, readByMe: true, readAt: new Date().toISOString() }
          : x
      )
    );
    if (!n.readByMe) {
      setUnreadCount((c) => Math.max(0, c - 1));
      // Best-effort persist; failures revert the optimistic count on
      // the next poll automatically.
      api.markNotificationRead(n.id).catch(() => {});
    }
    if (n.internalActionLink) navigate(n.internalActionLink);
  }

  async function handleMarkAllRead() {
    if (markingAll || unreadCount === 0) return;
    setMarkingAll(true);
    const optimisticCount = unreadCount;
    setUnreadCount(0);
    setNotifications((prev) =>
      prev.map((x) => (x.readByMe ? x : { ...x, readByMe: true, readAt: new Date().toISOString() }))
    );
    try {
      await api.markAllNotificationsRead();
    } catch {
      // Revert on failure; next count poll will reconcile too.
      setUnreadCount(optimisticCount);
    } finally {
      setMarkingAll(false);
    }
  }

  // The legacy bell capped display at "9+". The new endpoint caps at
  // 99+ which is more useful for accounts with real volume.
  const badgeLabel = unreadCount === 0
    ? ""
    : capped
      ? "99+"
      : unreadCount > 9
        ? `${unreadCount}`
        : `${unreadCount}`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const ariaLabel =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications";

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors relative"
      >
        <Bell className="w-5 h-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className={`absolute top-1.5 right-1.5 ${
              capped ? "min-w-[1.5rem] px-1" : "w-5"
            } h-5 rounded-full text-xs font-medium flex items-center justify-center text-white bg-amber-500`}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 animate-fade-in">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="font-semibold text-slate-800">Notifications</span>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {markingAll ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Mark all read
                </button>
              )}
              <span className="text-xs text-slate-400">
                {unreadCount} unread
              </span>
            </div>
          </div>

          {listLoading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              You're all caught up.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleClickNotification(n)}
                    className={`block w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                      !n.readByMe ? "bg-amber-50/40" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                          n.severity === "critical"
                            ? "bg-rose-600"
                            : n.severity === "high"
                              ? "bg-orange-500"
                              : n.severity === "medium"
                                ? "bg-amber-500"
                                : "bg-sky-500"
                        }`}
                        aria-hidden="true"
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm truncate ${
                            n.readByMe ? "text-slate-600" : "font-medium text-slate-900"
                          }`}
                        >
                          {n.title}
                        </div>
                        {n.domainName && (
                          <div className="text-xs text-slate-500 truncate">
                            {n.domainName}
                          </div>
                        )}
                        <div className="text-xs text-slate-400 mt-0.5">
                          {relativeTime(n.lastSeenAt)}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
            <Link
              to="/alerts"
              onClick={() => setOpen(false)}
              className="text-sm text-amber-600 hover:text-amber-700 font-medium"
            >
              View all alerts &rarr;
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
